// Dashboard — owner-readable summary. Pulls live numbers from invoices, bills,
// payments and bank balances for the current org.
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { TrendingUp, AlertTriangle, FileText, Landmark, ArrowRight, Receipt, DollarSign } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useOrg } from '../lib/org';
import { formatMoney } from '../lib/format';

export default function Dashboard() {
  const { currentOrg } = useOrg();
  const [invoices, setInvoices] = useState<any[]>([]);
  const [bills, setBills] = useState<any[]>([]);
  const [payments, setPayments] = useState<any[]>([]);
  const [bankAccounts, setBankAccounts] = useState<any[]>([]);
  const [quotes, setQuotes] = useState<any[]>([]);

  useEffect(() => {
    if (!currentOrg) return;
    (async () => {
      const [inv, b, pay, ba, qs] = await Promise.all([
        supabase.from('dodobooks_invoices').select('id,number,status,total,amount_paid,due_date,issue_date,kind').eq('org_id', currentOrg.id),
        supabase.from('dodobooks_bills').select('id,total,amount_paid,due_date,issue_date,status,kind').eq('org_id', currentOrg.id),
        supabase.from('dodobooks_payments').select('amount,paid_on,direction').eq('org_id', currentOrg.id).order('paid_on', { ascending: false }).limit(60),
        supabase.from('dodobooks_bank_accounts').select('id,name,balance,currency').eq('org_id', currentOrg.id).eq('is_archived', false),
        supabase.from('dodobooks_quotes').select('id,status').eq('org_id', currentOrg.id),
      ]);
      setInvoices(inv.data ?? []);
      setBills(b.data ?? []);
      setPayments(pay.data ?? []);
      setBankAccounts(ba.data ?? []);
      setQuotes(qs.data ?? []);
    })();
  }, [currentOrg]);

  const today = new Date(); today.setHours(0, 0, 0, 0);
  const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
  const sixMonthStart = new Date(today.getFullYear(), today.getMonth() - 5, 1);

  const arOutstanding = useMemo(() => invoices.filter((i) => i.kind !== 'credit_note')
    .reduce((s, i) => s + Math.max(Number(i.total) - Number(i.amount_paid), 0), 0), [invoices]);
  const apOutstanding = useMemo(() => bills
    .reduce((s, i) => s + Math.max(Number(i.total) - Number(i.amount_paid), 0), 0), [bills]);
  const overdueCount = useMemo(() => invoices.filter((i) => i.due_date && new Date(i.due_date) < today && Number(i.amount_paid) < Number(i.total)).length, [invoices, today]);
  const paidThisMonth = useMemo(() => payments.filter((p) => p.direction === 'in' && new Date(p.paid_on) >= monthStart).reduce((s, p) => s + Number(p.amount), 0), [payments, monthStart]);
  const spentThisMonth = useMemo(() => payments.filter((p) => p.direction === 'out' && new Date(p.paid_on) >= monthStart).reduce((s, p) => s + Number(p.amount), 0), [payments, monthStart]);
  const bankBalance = useMemo(() => bankAccounts.reduce((s, b) => s + Number(b.balance ?? 0), 0), [bankAccounts]);
  const openQuotes = useMemo(() => quotes.filter((q) => ['draft', 'sent', 'viewed'].includes(q.status)).length, [quotes]);

  const revenue6mo = useMemo(() => {
    const months: { label: string; v: number }[] = [];
    for (let i = 0; i < 6; i++) {
      const d = new Date(today.getFullYear(), today.getMonth() - 5 + i, 1);
      months.push({ label: d.toLocaleString('en-ZA', { month: 'short' }), v: 0 });
    }
    for (const p of payments) {
      if (p.direction !== 'in') continue;
      const pd = new Date(p.paid_on);
      if (pd < sixMonthStart) continue;
      const idx = (pd.getFullYear() - sixMonthStart.getFullYear()) * 12 + (pd.getMonth() - sixMonthStart.getMonth());
      if (idx >= 0 && idx < 6) months[idx].v += Number(p.amount);
    }
    return months;
  }, [payments, today, sixMonthStart]);

  const maxRev = Math.max(...revenue6mo.map((m) => m.v), 1);

  return (
    <div className="space-y-5">
      <div>
        <h1 className="h-page flex items-center gap-2"><TrendingUp size={22} /> Dashboard</h1>
        <p className="text-sm text-text-mute mt-1">Where your business stands right now — at a glance.</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Card title="Cash in bank" value={formatMoney(bankBalance, 'ZAR')} icon={<Landmark size={16} />} link="/bank" />
        <Card title="You're owed" value={formatMoney(arOutstanding, 'ZAR')} icon={<DollarSign size={16} />} link="/reports/aging" highlight={arOutstanding > 0 ? 'emerald' : undefined} />
        <Card title="You owe" value={formatMoney(apOutstanding, 'ZAR')} icon={<Receipt size={16} />} link="/bills" />
        <Card title="Overdue invoices" value={String(overdueCount)} icon={<AlertTriangle size={16} />} link="/invoices" highlight={overdueCount > 0 ? 'rust' : undefined} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[2fr_1fr] gap-3">
        <div className="card">
          <h2 className="h-section text-base mb-3">Revenue · last 6 months</h2>
          <div className="flex items-end gap-2 h-32">
            {revenue6mo.map((m, i) => (
              <div key={i} className="flex-1 flex flex-col items-center gap-1">
                <div className="w-full bg-emerald/40 rounded-t" style={{ height: `${(m.v / maxRev) * 100}%`, minHeight: m.v > 0 ? 4 : 0 }} title={formatMoney(m.v, 'ZAR')} />
                <div className="text-[10px] text-text-mute">{m.label}</div>
              </div>
            ))}
          </div>
          <div className="text-xs text-text-mute mt-2">Peak month: {formatMoney(maxRev, 'ZAR')}</div>
        </div>
        <div className="card">
          <h2 className="h-section text-base mb-3">This month</h2>
          <Row label="Paid in" value={formatMoney(paidThisMonth, 'ZAR')} color="text-emerald" />
          <Row label="Paid out" value={formatMoney(spentThisMonth, 'ZAR')} color="text-rust" />
          <div className="border-t border-line mt-2 pt-2">
            <Row label="Net cash" value={formatMoney(paidThisMonth - spentThisMonth, 'ZAR')} color={paidThisMonth - spentThisMonth >= 0 ? 'text-emerald font-bold' : 'text-rust font-bold'} />
          </div>
          <div className="mt-3 text-xs">
            <Link to="/reports/pl" className="text-emerald hover:underline inline-flex items-center gap-1">Full P&L <ArrowRight size={11} /></Link>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <QuickLink to="/quotes" title={`${openQuotes} open quote${openQuotes === 1 ? '' : 's'}`} icon={<FileText size={16} />} />
        <QuickLink to="/invoices/new" title="Send a new invoice" icon={<DollarSign size={16} />} />
        <QuickLink to="/payroll" title="Run payroll" icon={<Receipt size={16} />} />
      </div>
    </div>
  );
}

function Card({ title, value, icon, link, highlight }: { title: string; value: string; icon: any; link: string; highlight?: 'emerald' | 'rust' }) {
  const colour = highlight === 'emerald' ? 'text-emerald' : highlight === 'rust' ? 'text-rust' : 'text-text';
  return (
    <Link to={link} className="card hover:border-line-strong transition-colors">
      <div className="flex items-center gap-2 text-text-mute text-xs uppercase tracking-widest font-mono">{icon} {title}</div>
      <div className={`font-display text-2xl font-bold mt-1 ${colour}`}>{value}</div>
    </Link>
  );
}
function Row({ label, value, color }: { label: string; value: string; color?: string }) {
  return (
    <div className="flex items-center justify-between py-1 text-sm">
      <span className="text-text-mute">{label}</span>
      <span className={`font-mono ${color ?? ''}`}>{value}</span>
    </div>
  );
}
function QuickLink({ to, title, icon }: { to: string; title: string; icon: any }) {
  return (
    <Link to={to} className="card flex items-center justify-between hover:border-line-strong transition-colors">
      <div className="flex items-center gap-2 text-sm">{icon}{title}</div>
      <ArrowRight size={14} className="text-text-mute" />
    </Link>
  );
}
